import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import NumberInput from '../../components/NumberInput';
import { useNotification } from '../../contexts/NotificationContext';

function BundlesContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const [bundles, setBundles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchServices, setBranchServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [form, setForm] = useState({
    branch_id: '',
    name: '',
    bundle_price: '',
    total_duration_minutes: '',
    item_service_ids: [],
  });

  const fetchBranches = async () => {
    try {
      const data = await api.getManagerBranches();
      setBranches(data || []);
      if (data?.length > 0 && !selectedBranchId) setSelectedBranchId(data[0].id);
    } catch (err) { console.error(err); }
  };

  const fetchBundles = async () => {
    try {
      const data = await api.getManagerBundles();
      setBundles(data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchBranchServices = async (branchId) => {
    if (!branchId) { setBranchServices([]); return; }
    try {
      const data = await api.getBranchServices(branchId);
      setBranchServices(data || []);
    } catch (err) { setBranchServices([]); }
  };

  useEffect(() => { fetchBranches(); }, []);
  useEffect(() => { fetchBundles(); }, []);

  const openCreate = () => {
    const bid = selectedBranchId || branches[0]?.id;
    setForm({ branch_id: bid || '', name: '', bundle_price: '', total_duration_minutes: '', item_service_ids: [] });
    setEditing('create');
    fetchBranchServices(bid || null);
  };

  const openEdit = (b) => {
    setForm({
      branch_id: b.branch_id,
      name: b.name,
      bundle_price: b.bundle_price != null ? String(b.bundle_price) : '',
      total_duration_minutes: b.total_duration_minutes != null ? String(b.total_duration_minutes) : '',
      item_service_ids: (b.items || []).map(i => i.service_id),
    });
    setEditing(b.id);
    fetchBranchServices(b.branch_id);
  };

  const handleBranchChange = (branchId) => {
    setForm(f => ({ ...f, branch_id: branchId, item_service_ids: [] }));
    fetchBranchServices(branchId ? parseInt(branchId) : null);
  };

  const toggleServiceInBundle = (serviceId) => {
    setForm(f => {
      const ids = f.item_service_ids.includes(serviceId)
        ? f.item_service_ids.filter(id => id !== serviceId)
        : [...f.item_service_ids, serviceId];
      return { ...f, item_service_ids: ids };
    });
  };

  const save = async () => {
    if (!form.branch_id || !form.name?.trim()) { showAlert(t('admin.branchAndNameRequired')); return; }
    if (!form.item_service_ids.length) { showAlert(t('admin.bundles.atLeastOneService')); return; }
    try {
      const payload = {
        branch_id: parseInt(form.branch_id),
        name: form.name.trim(),
        bundle_price: form.bundle_price ? parseFloat(form.bundle_price) : null,
        total_duration_minutes: form.total_duration_minutes ? parseInt(form.total_duration_minutes, 10) : null,
        items: form.item_service_ids.map((sid, i) => ({ service_id: sid, sort_order: i })),
      };
      if (editing === 'create') await api.createManagerBundle(payload);
      else await api.updateManagerBundle(editing, { name: payload.name, bundle_price: payload.bundle_price, total_duration_minutes: payload.total_duration_minutes, items: payload.items });
      setEditing(null);
      fetchBundles();
    } catch (err) { showAlert(err.message); }
  };

  const remove = async (id) => {
    showConfirm(t('admin.bundles.deleteConfirm'), async () => {
      try { await api.deleteManagerBundle(id); fetchBundles(); }
      catch (err) { showAlert(err.message); }
    });
  };

  const branchById = (id) => branches.find(b => b.id === id);
  const displayBundles = selectedBranchId ? bundles.filter(b => b.branch_id === selectedBranchId) : bundles;

  return (
    <AdminLayout role="manager">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2rem' }}>
        <div>
          <h1 style={S.pageTitle}>{t('admin.bundles.title')}</h1>
          <div style={{ width: '40px', height: '2px', background: T.orange }} />
        </div>
        {branches.length > 0 && (
          <button style={S.btnOrange} onClick={openCreate}
            onMouseEnter={e => e.target.style.background = T.orangeHov}
            onMouseLeave={e => e.target.style.background = T.orange}>+ {t('admin.bundles.add')}</button>
        )}
      </div>

      {branches.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '0.78rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>{t('admin.selectBranch')}</label>
          <select value={selectedBranchId || ''} onChange={e => setSelectedBranchId(parseInt(e.target.value) || null)} style={{ ...S.select, width: 'auto' }}>
            <option value="">{t('admin.allBranches')}</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      )}

      {editing && (
        <div style={S.modal}>
          <div style={{ ...S.modalBox, maxWidth: '520px' }}>
            <h2 style={S.modalTitle}>{editing === 'create' ? t('admin.bundles.new') : t('admin.bundles.edit')}</h2>

            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, marginBottom: '0.4rem' }}>{t('admin.selectBranch')}</label>
            <select value={form.branch_id} onChange={e => handleBranchChange(e.target.value)} style={S.select}>
              <option value="">{t('admin.chooseBranch')}</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>

            <input placeholder={t('admin.bundles.namePlaceholder')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={S.input} />

            <NumberInput placeholder={t('admin.bundles.bundlePriceOptional')} value={form.bundle_price} onChange={v => setForm({ ...form, bundle_price: v })} inputStyle={S.input} min={0} step={0.01} />
            <NumberInput placeholder={t('admin.bundles.totalDurationOptional')} value={form.total_duration_minutes} onChange={v => setForm({ ...form, total_duration_minutes: v })} inputStyle={S.input} min={0} step={15} />

            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, marginBottom: '0.4rem' }}>{t('admin.bundles.selectServices')}</label>
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: `1px solid ${T.border}`, borderRadius: '8px', padding: '0.5rem' }}>
              {branchServices.length === 0 && form.branch_id && <p style={{ fontSize: '0.85rem', color: T.muted }}>{t('admin.bundles.noServicesInBranch')}</p>}
              {branchServices.map(svc => {
                const checked = form.item_service_ids.includes(svc.id);
                return (
                  <label key={svc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleServiceInBundle(svc.id)} />
                    <span style={{ fontSize: '0.9rem' }}>{svc.name} — {svc.price} EGP, {svc.duration_minutes} min</span>
                  </label>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button onClick={save} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
              <button onClick={() => setEditing(null)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: T.muted }}>{t('common.loading')}</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>{[t('admin.id'), t('admin.bundles.name'), t('admin.branch'), t('admin.bundles.servicesCount'), t('admin.bundles.price'), t('admin.actions')].map((h, i) => <th key={i} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {displayBundles.map(b => (
                <tr key={b.id} onMouseEnter={e => e.currentTarget.style.background = 'rgba(27,213,173,0.04)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={S.td}>{b.id}</td>
                  <td style={{ ...S.td, fontWeight: 500, color: T.white }}>{b.name}</td>
                  <td style={S.td}>{branchById(b.branch_id)?.name ?? b.branch_id}</td>
                  <td style={S.td}>{(b.items || []).length}</td>
                  <td style={{ ...S.td, color: T.orange }}>{b.bundle_price != null ? `${b.bundle_price} EGP` : '—'}</td>
                  <td style={S.td}>
                    <button style={S.btnSmall} onClick={() => openEdit(b)}>{t('common.edit')}</button>
                    <button style={S.btnDanger} onClick={() => remove(b.id)}>{t('common.delete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {displayBundles.length === 0 && <p style={{ color: T.muted, padding: '1rem 0' }}>{t('admin.bundles.none')}</p>}
        </div>
      )}
    </AdminLayout>
  );
}

export default function Bundles() {
  return <ProtectedRoute requireSalonManager><BundlesContent /></ProtectedRoute>;
}
